import { FC } from 'react';
import { useTheme } from '../context/ThemeContext.js';
import { Cloud, Brain, Shield } from 'lucide-react';

interface HomePageProps {
  onPageChange: (page: string) => void;
}

export const HomePage: FC<HomePageProps> = ({ onPageChange }) => {
  const { theme } = useTheme();
  
  // Theme-based styles
  const themeStyles = {
    light: {
      background: 'bg-gradient-to-b from-gray-50 to-gray-100',
      text: 'text-gray-900',
      card: 'bg-white hover:bg-gray-50',
      cardText: 'text-gray-700',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
      buttonText: 'text-white',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    },
    dark: {
      background: 'bg-gradient-to-b from-gray-900 to-gray-800',
      text: 'text-white',
      card: 'bg-gray-800 hover:bg-gray-700',
      cardText: 'text-gray-300',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
      buttonText: 'text-white',
      iconBg: 'bg-blue-900 bg-opacity-50',
      iconText: 'text-blue-200'
    }
  };

  // Get current theme styles, default to dark if in system mode
  const currentTheme = theme === 'system' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  const styles = themeStyles[currentTheme as keyof typeof themeStyles] || themeStyles.dark;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${styles.background} ${styles.text}`}>
      {/* Hero Section */}
      <div className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-5xl font-bold mb-6">Welcome to Clysera</h1>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
          Empowering businesses with innovative technology solutions for the digital future.
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => onPageChange('contact')}
            className={`${styles.button} font-bold py-3 px-8 rounded-lg text-lg transition duration-300`}
          >
            Get Started
          </button>
          <button
            onClick={() => onPageChange('services')}
            className="bg-transparent border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-bold py-3 px-8 rounded-lg text-lg transition duration-300"
          >
            Our Services
          </button>
        </div>
      </div>
      
      {/* Services Preview */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Our Services</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: 'Cloud Solutions',
              description: 'Comprehensive cloud services including migration, management, and optimization across all major platforms.',
              icon: <Cloud className="w-8 h-8" />
            },
            {
              title: 'AI & ML',
              description: 'Cutting-edge artificial intelligence and machine learning solutions to transform your business.',
              icon: <Brain className="w-8 h-8" />
            },
            {
              title: 'Cybersecurity',
              description: 'Protect your digital assets with our comprehensive security solutions and best practices.',
              icon: <Shield className="w-8 h-8" />
            }
          ].map((service, index) => (
            <div 
              key={index} 
              className={`${styles.card} p-6 rounded-lg transition-all duration-300 hover:shadow-lg cursor-pointer flex flex-col items-center text-center`}
              onClick={() => onPageChange('services')}
            >
              <div className={`w-16 h-16 rounded-full ${styles.iconBg} flex items-center justify-center text-3xl mb-6`}>
                <span className={styles.iconText}>
                  {service.icon}
                </span>
              </div>
              <h3 className="text-xl font-semibold mb-3 w-full">{service.title}</h3>
              <p className={`${styles.cardText} w-full`}>{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className={`py-16 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's discuss how Clysera can help you achieve your technology goals.
          </p>
          <button
            onClick={() => onPageChange('contact')}
            className={`${styles.button} font-bold py-3 px-8 rounded-lg text-lg transition duration-300`}
          >
            Contact Us Today
          </button>
        </div>
      </div>
    </div>
  );
};
